// @ts-ignore
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../core/theme';
import { FontAwesomeIcon as FA } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash';
import { faFolder } from '@fortawesome/free-solid-svg-icons';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons/faPenToSquare';
import { UIDropDownItem, UIHoverIconMenu } from '../../core/ui/UIDropDown';
import { motion } from 'framer-motion';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../../tendril-v2/src/Presentation/Common/Components/ui/tooltip';
import { toast } from '../../../../tendril-v2/src/Presentation/Common/Components/ui/use-toast';
import ScrollableSpan from "./ScrollableSpan";

const List = styled(motion.ul)<{
  height?: string;
}>`
  display: flex;
  flex-direction: column;
  ${({ height }) => ({
    minHeight: height,
    maxHeight: height,
    overflowY: 'scroll',
  })};
  background-color: ${colors.lightGray};
  border: 1px solid ${colors.lightGray};
  border-radius: 0 0 0.5rem 0.5rem;
  overflow-y: auto;
`;

const ListButton = styled.li`
  display: flex;
  gap: 0.5em;
  align-items: center;
  width: 100%;
  padding: 1em 1.5em;
  label {
    flex: 1;
  }
  background-color: white;
  cursor: pointer;
  border-bottom: 1px solid ${colors.lightGray};
  transition: 0.25s;
  &.selected {
    color: white;
    background-color: ${colors.lightBlue};
  }
  &.exceed {
    color: ${colors.midBlue};
    background-color: #dadada;
    pointer-events: none;
    cursor: not-allowed;
  }
`;

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any[];
  radioBtn: boolean;
  setOptionSelected: (value: any) => void;
  setCurrentSelection?: (element: any) => void;
  setVisible: (value: any) => void;
  deleteItem?: (e: any) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  editItem?: (e: any) => void;
  newItemTitle?: string;
  height?: string;
  multipleSelection?: boolean;
  colorScroll?: string;
  rowIcon?: any;
}

type ListItem = { id: string; select: boolean; title: string; tendrilId: string; tendrilUserName: string };

const GenericList: React.FC<Props> = ({
  data,
  radioBtn,
  setVisible,
  deleteItem,
  editItem,
  newItemTitle,
  setOptionSelected,
  setCurrentSelection,
  height,
  multipleSelection = false,
  rowIcon,
}) => {
  const [dataList, setDataList] = useState([] as any[]);
  const [isHovered, setIsHovered] = useState(null);
  const selectedCount = useRef(0);
  const [selectDisabled, setSelectDisabled] = useState(false);
  const MAX_SELECTION = 5;

  useEffect(() => {
    if (newItemTitle) {
      setDataList([
        ...data.map((item: ListItem) => {
          return { ...item, select: false };
        }),
        {
          id: 'newItem',
          organizationId: 'newItem',
          title: newItemTitle,
          select: true,
        },
      ]);
    } else {
      setDataList(
        data.map((item: ListItem) => {
          return { ...item, select: false };
        }),
      );
    }
  }, [data]);

  const handleChangeCheckBox = (id: string, tendrilId: null | string) => {
    setDataList(
      dataList.map((listItem: ListItem) => {
        if (multipleSelection) {
          if (id === listItem.id) {
            listItem.select = !listItem.select;
          }
        } else {
          listItem.select = id === listItem.id;
        }
        if (id === 'newItem') setVisible(true);
        if (listItem.tendrilId && tendrilId && listItem.tendrilId !== tendrilId) {
          listItem.select = false;
        }

        return listItem;
      }),
    );
    // These lines limit the List multi-selection according to the MAX_SELECTION constant written above.
    const selectedItems = dataList.filter((element) => {
      return element.select === true;
    });

    selectedCount.current = selectedItems?.length;
    setSelectDisabled(selectedCount.current >= MAX_SELECTION);
  };
  useEffect(() => {
    setOptionSelected(dataList);

    if (selectedCount.current >= MAX_SELECTION) {
      multipleSelection = false;
      toast({
        itemID: '0',
        variant: 'default',
        title: 'Limit reached!',
        description: 'You have reached the maximum selectable lists of 5',
      });
    }
  }, [dataList]);

  const handleChange = (id: string, tendrilId: null | string, element: unknown) => {
    if (element !== undefined && setCurrentSelection !== undefined) {
      setCurrentSelection(element);
    }
    handleChangeCheckBox(id, tendrilId);
  };

  return (
    <List
      height={height}
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      animate={{ y: [-20, 10, 0], opacity: 1 }}
      transition={{ ease: 'easeIn', duration: 0.5 }}
    >
      {dataList.map((element: ListItem, index) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <ListButton
                data-qa-id={`list_${element.title.split(' ').join('_')}`}
                key={`list_btn_${element.title.split(' ').join('_')}_${element.id}_${index}`}
                className={`${(element.select && 'selected') || (selectDisabled && 'exceed')}`}
                value={
                  !multipleSelection && element.title !== newItemTitle ? element.title.split('+')[0] : element.title
                }
                onClick={() => handleChange(element.id, element.tendrilId || null, element)}
                data-for={`menu_${element.title.split(' ').join('_')}`}
              >
                <>
                  {element.select ? (
                    <FA icon={['fas', radioBtn ? 'circle-dot' : 'square-check']} />
                  ) : element.tendrilUserName ? (
                    <FA icon={faFolder} />
                  ) : (
                    <FA icon={['fas', radioBtn ? 'circle' : 'square']} />
                  )}
                  {
                    <ScrollableSpan>
                      {!multipleSelection && element.title !== newItemTitle
                        ? element.title.split('+')[0]
                        : element.title}
                    </ScrollableSpan>
                  }
                  {rowIcon && (
                    <div
                      className="table-container"
                      onMouseEnter={() => setIsHovered(element.id)}
                      onMouseLeave={() => setIsHovered(null)}
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        flex: 1,
                      }}
                    >
                      <FA
                        icon={rowIcon}
                        className="circle-dot fa-lg icon-hover"
                        style={{
                          position: 'absolute',
                          right: '10px',
                        }}
                      />
                      {isHovered === element.id && (
                        <div
                          style={{
                            position: 'absolute',
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            right: '35px',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {element.tendrilUserName}
                        </div>
                      )}
                    </div>
                  )}
                  {element.id !== 'newItem' && (deleteItem || editItem) && (
                    <UIHoverIconMenu dataTitle={`menu_${element.title.split(' ').join('_')}`}>
                      {editItem && (
                        <UIDropDownItem
                          data-qa-id={`edit_${element.title.split(' ').join('_')}`}
                          key={`UIDropDownItemEdit_${element.id}`}
                          className={`${element.select && 'selected'}`}
                          value={
                            !multipleSelection && element.title !== newItemTitle
                              ? element.title.split('+')[0]
                              : element.title
                          }
                          onClick={() => editItem(element)}
                        >
                          <FA icon={faPenToSquare} />
                          Edit
                        </UIDropDownItem>
                      )}
                      {deleteItem && (
                        <UIDropDownItem
                          data-qa-id={`delete_${element.title.split(' ').join('_')}`}
                          key={`UIDropDownItemDelete_${element.id}`}
                          className={`${element.select && 'selected'}`}
                          value={
                            !multipleSelection && element.title !== newItemTitle
                              ? element.title.split('+')[0]
                              : element.title
                          }
                          onClick={() => deleteItem(element)}
                        >
                          <FA icon={faTrash} />
                          Delete
                        </UIDropDownItem>
                      )}
                    </UIHoverIconMenu>
                  )}
                </>
              </ListButton>
            </TooltipTrigger>
            <TooltipContent>{element.title}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ))}
    </List>
  );
};
export default GenericList;
